package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.billing.WorkplacePlan
import com.evoapps.evoshift.domain.util.secureRandomBytes
import kotlin.time.Instant

/**
 * SH-239: the links to evoshift.app that EVoShift puts where people outside the app see it, with
 * UTM parameters naming where (functions/growthLinks.js has the same for the e-mails, the PDF and
 * the job page). Nothing about the reader or the workplace is in a link; the referral code is a
 * random one ([ReferralCodes]).
 */
object GrowthLinks {
    const val SITE = "https://evoshift.app/"

    /** Where a "made with EVoShift" line was seen (utm_source). */
    enum class Source(val id: String) {
        AVAILABILITY_FORM("availability_form")
    }

    /** The "made with EVoShift" line's link seen at [source]. */
    fun madeWith(source: Source): String =
        "$SITE?utm_source=${source.id}&utm_medium=referral&utm_campaign=made_with"

    /** "Recommend EVoShift": the link an employee shares, with their [referralCode]. */
    fun referral(referralCode: String): String =
        "$SITE?ref=$referralCode&utm_source=employee_share&utm_medium=referral"
}

/** SH-239: the opaque code in a person's "Recommend EVoShift" link: the join codes' alphabet and length. */
object ReferralCodes {
    fun generate(randomBytes: (Int) -> ByteArray = ::secureRandomBytes): String = InviteCodes.generate(randomBytes)

    /** The shape firestore.rules accept (8 characters, no look-alikes). */
    fun isValid(code: String?): Boolean =
        code != null && code.length == InviteCodes.LENGTH && code.all { it in InviteCodes.ALPHABET }
}

/**
 * SH-239: the "Made with EVoShift" line on the rosters (`settings.showEvoShiftFooter`): on by
 * default; it may be hidden only while the owner pays for a plan, never on the trial, when the plan
 * is unknown or has ended (firestore.rules and functions/growthLinks.js decide the same).
 */
object MadeWithFooter {
    fun canHide(plan: WorkplacePlan?, now: Instant): Boolean = plan?.tierAt(now) == PlanTier.PREMIUM

    fun shows(settings: WorkplaceSettings, plan: WorkplacePlan?, now: Instant): Boolean =
        settings.showEvoShiftFooter || !canHide(plan, now)
}

/**
 * SH-239: how much of a workplace's team uses the app, for the owner's "Invite the team" card:
 * [total] people with an active engagement besides the owner, [joined] of them with their own
 * account (not a profile the managers keep for them), and whether a month was published.
 */
data class TeamJoin(val hasPublished: Boolean, val total: Int, val joined: Int) {
    /** Most of the team joined: [JOINED_SHARE_PERCENT] or more. */
    val mostJoined: Boolean get() = total > 0 && joined * 100 >= total * JOINED_SHARE_PERCENT

    /** The card: after the first published month, while someone can still be invited and most have not joined. */
    val invitePrompted: Boolean get() = hasPublished && total > joined && !mostJoined

    companion object {
        const val JOINED_SHARE_PERCENT = 60
    }
}
